import { createDefaultSentryBeforeSend } from '@littlebird/logger/sentry';
import * as Sentry from '@sentry/react';
import { reaction } from 'mobx';

import { getEnvVar, isDev, isRunningInLocalhost, isTest } from '@/env';
import { electronApis } from '@/features/electron-api';
import { userStore } from '@/stores/user.store';
import {
  getCurrentPlatform,
  inDesktop,
  inElectron,
} from '@/utils/capabilities';

import { getBrowser } from './platform';

const isStagingBuild = getEnvVar('VITE_IS_STAGING_BUILD_FOR_DESKTOP');
const env = isStagingBuild ? 'staging-build' : getEnvVar('VITE_ENV');

const getSystemInfo = async () => ({
  platform: getCurrentPlatform(),
  env,
  appVersion: inElectron()
    ? await window.systemInfo.appVersion()
    : 'web-unavailable',
  appWindow: inDesktop() ? window.systemInfo.currentWindow.label : 'main',
  osVersion: inElectron()
    ? await window.systemInfo.systemVersion
    : 'web-unavailable',
  osType: inElectron() ? await window.systemInfo.osType() : 'web-unavailable',
  osArch: inElectron() ? window.systemInfo.arch : 'web-unavailable',
  device_id: inElectron()
    ? await electronApis.system.getDeviceId()
    : 'web-unavailable',
  session_id: inElectron()
    ? await electronApis.system.getSessionId()
    : 'web-unavailable',
  browser: getBrowser(),
  userAgent: navigator?.userAgent,
});

const IGNORED_ERRORS = [
  /not connected to internet/,
  /ClientInactiveError/i,
  /^Cannot make HTTP requests while client is inactive$/i,
  /^WebSocket connection lost$/i,
];

export const initializeSentry = async () => {
  const systemInfo = await getSystemInfo();
  Sentry.init({
    dsn: 'https://96f904e733472b528b0154cd44021a41@o4507576431542272.ingest.us.sentry.io/4508289503789056',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    enableLogs: true,
    // Tracing
    tracesSampleRate: 0.1, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['/^.*tauri.*$/', /^https:\/\/lilbird\.co/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    enabled: !isDev && !isRunningInLocalhost && !isTest,
    // Also set here: without SENTRY_UPLOAD the plugin is off and injects none.
    release: __APP_VERSION__,
    // Empty outside CI; omit rather than label the build with a stand-in.
    dist: __APP_COMMIT__ || undefined,
    environment: env,
    ignoreErrors: IGNORED_ERRORS,
    beforeSend: createDefaultSentryBeforeSend(),
  });
  for (const [key, value] of Object.entries(systemInfo)) {
    Sentry.setTag(key, value);
  }
  Sentry.setContext('system', systemInfo);
  initializeTracking();
};

export const identifyPersonInSentry = async (user?: Sentry.User) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    email: user.email,
    username: user.name,
  });
};

function initializeTracking() {
  const currentUser = userStore.user;
  if (currentUser?.email) {
    identifyPersonInSentry(currentUser);
  }

  reaction(
    () => userStore.user,
    (user) => {
      if (user?.email) {
        identifyPersonInSentry(user);
      } else {
        identifyPersonInSentry();
      }
    }
  );
}
